// Unified permission/scope registry — the single source of truth for
// "which microservices exist and what access levels each one has."
//
// WHY THIS EXISTS
// ---------------
// Access used to be a set of disconnected, copy-pasted axes on the user doc
// (restricted.{role, operatorRank, operatorRole, recordingsRole, logsRole}),
// each with its own resolver, its own duplicated firestore.rules helpers, and
// its own admin page. There was no one place that answered "what can this user
// reach?" and adding a new gated feature meant cloning the whole pattern again.
//
// This registry unifies that. Every gated microservice is declared once here
// with its ordered access levels; per-user grants live in a single map on the
// user doc (see GRANT MODEL below); firestore.rules reads that map through
// generic helpers; and one admin UI renders the registry × users matrix.
//
// It is intentionally consistent with the existing TEAM_CAPABILITIES layer in
// src/pm/constants.ts: STABLE STRING KEYS (never enum positions), so adding or
// removing a service/level never shifts stored data.
//
// GRANT MODEL (on the user doc)
// -----------------------------
//   restricted.permissions = {
//     "operator":       { "level": "super_admin" },
//     "recordings":     { "level": "agent" },   // scope implicit (token email)
//     "intake_logs":    { "level": "viewer" },
//     "exec_dashboard": { "level": "viewer" },
//     // future scoped grant:
//     // "recordings":  { "level": "supervisor", "scope": { "campaigns": ["acme"] } }
//   }
//
// The grant is an OBJECT with an optional `scope`, not a bare string, so a
// future per-client/campaign restriction can be expressed and enforced in
// rules (map.get('scope',{}).get('campaigns',[]).hasAny([...])) WITHOUT a
// second breaking migration. During the deprecation window a value may still
// be a raw legacy string; permLevel() tolerates both.
//
// SECURITY NOTE: `permissions` lives UNDER `restricted`. The users/{uid}
// update rule blocks any non-super_admin write that touches `restricted`, so a
// user cannot self-grant a permission — the escalation surface stays closed.

export interface ServiceDef {
  /** Stable Firestore key — NEVER rename (it is stored on user docs). */
  key: string;
  /** Human label for the admin matrix. */
  label: string;
  /** One-line description for the admin matrix. */
  description: string;
  /** Access levels, ordered WEAKEST → STRONGEST. Used for ordered comparison
   *  in TS (hasAtLeast) and to populate the admin picker. Rules can't index
   *  arrays, so rule-side checks use explicit allow-lists per call site. */
  levels: string[];
  /** True if this service enforces a per-user `scope` map (e.g. future
   *  campaign-restricted views). Recordings' agent scope is derived from the
   *  auth token, not stored, so recordings leaves this false. */
  hasScopes?: boolean;
  /** True if this grant is only meaningful for operators (drives shell/nav
   *  coupling and admin-UI hints). Enforcement still happens in rules. */
  requiresOperator?: boolean;
}

// The canonical service list. Keys map 1:1 to restricted.permissions.<key>.
export const SERVICE_REGISTRY: ServiceDef[] = [
  {
    key: 'operator',
    label: 'Operator suite (PM)',
    description: 'Projects, workstreams, tasks, teams, internal staff.',
    levels: ['standard', 'admin', 'super_admin'],
    requiresOperator: true,
  },
  {
    key: 'recordings',
    label: 'Call recordings',
    description: 'Five9 recording metadata + audited playback.',
    levels: ['agent', 'supervisor', 'qa_lead', 'admin'],
    hasScopes: true,
  },
  {
    key: 'intake_logs',
    label: 'Intake logs',
    description: 'CloudWatch intake-form dashboard + audited raw drill-down.',
    levels: ['viewer', 'admin'],
    requiresOperator: true,
  },
  {
    key: 'exec_dashboard',
    label: 'Executive dashboard',
    description: 'Cross-client call-volume + telephony KPIs (exec/ownership).',
    levels: ['viewer', 'admin'],
    requiresOperator: true, // v1: exec ⊆ operator (shell selection keys on role)
  },
  {
    key: 'finance',
    label: 'Financials',
    description: 'Revenue, P&L, margins, unit economics (exec-dashboard Financials silo). Stricter than exec_dashboard — dollar figures render only with this grant.',
    levels: ['viewer', 'admin'],
    requiresOperator: true, // finance ⊆ exec: the silo lives inside the exec dashboard
  },
  // Declared for the admin matrix UI + future rule-level enforcement. These
  // are still enforced by isOperator() at the rule level today (see the
  // firestore.rules header note) — the registry entry lets the matrix show and
  // eventually own them without another rewrite.
  {
    key: 'teams',
    label: 'Client teams',
    description: 'Create/manage client team workspaces + membership.',
    levels: ['none', 'manage'],
    requiresOperator: true,
  },
  {
    key: 'receiving_numbers',
    label: 'Receiving numbers',
    description: 'Manage the receiving-numbers registry.',
    levels: ['none', 'manage'],
    requiresOperator: true,
  },
  {
    // Marketing-site CMS. v1 owns the redirect table (the /pay-style short
    // links that used to live in the WordPress url-shortify plugin); further
    // site-content surfaces will hang off the same grant rather than each
    // inventing a new axis.
    //
    // `editor` can add, edit, and disable redirects. `admin` can additionally
    // DELETE them — deleting a live short link silently breaks whatever is
    // already printed on an invoice, so it is deliberately the higher bar.
    key: 'website',
    label: 'Website CMS',
    description: 'Marketing-site redirects and short links (rolloverrep.com).',
    levels: ['viewer', 'editor', 'admin'],
    requiresOperator: true,
  },
  {
    key: 'feedback',
    label: 'Feedback inbox',
    description: 'Read and triage submitted app feedback.',
    levels: ['none', 'manage'],
    requiresOperator: true,
  },
];

const BY_KEY: Record<string, ServiceDef> = Object.fromEntries(
  SERVICE_REGISTRY.map((s) => [s.key, s]),
);

/** Look up a service definition by key. */
export function serviceDef(service: string): ServiceDef | undefined {
  return BY_KEY[service];
}

/** A single grant. `scope` is reserved for services with hasScopes. */
export interface Grant {
  level: string;
  scope?: Record<string, unknown>;
}

// A loose shape for the user's stored `restricted` map. `permissions` is typed
// as unknown-valued because it comes straight off a Firestore doc — the value
// at each service key may be the new Grant object, a bare legacy string, or
// malformed data. permLevel/permScope narrow it at runtime.
type RestrictedLike =
  | { permissions?: Record<string, unknown> }
  | null
  | undefined;

/**
 * Canonical read of a user's granted level for a service. Prefers the new
 * `permissions` map (object or legacy-string value). Returns null when the
 * user has no grant for that service (or the stored value is malformed).
 */
export function permLevel(restricted: RestrictedLike, service: string): string | null {
  const g = restricted?.permissions?.[service];
  if (!g) return null;
  if (typeof g === 'string') return g || null;
  const level = (g as Grant).level;
  return typeof level === 'string' && level ? level : null;
}

/**
 * Ordered comparison against a service's `levels`. True iff the user's granted
 * level is at least `min`. Unknown service or ungranted → false.
 */
export function hasAtLeast(level: string | null, min: string, service: string): boolean {
  const def = BY_KEY[service];
  if (!def || !level) return false;
  const a = def.levels.indexOf(level);
  const b = def.levels.indexOf(min);
  return a >= 0 && b >= 0 && a >= b;
}

/** Read a service grant's optional scope map (null if none / legacy string). */
export function permScope(
  restricted: RestrictedLike,
  service: string,
): Record<string, unknown> | null {
  const g = restricted?.permissions?.[service];
  if (!g || typeof g === 'string') return null;
  const scope = (g as Grant).scope;
  return scope && typeof scope === 'object' ? scope : null;
}
